import React, { useEffect } from "react";
import Modal from "react-responsive-modal";
import { Ban, ArrowLeft, ArrowRight } from "lucide-react";
import { SpinnerCircularFixed } from "spinners-react";
import { PartialCancellationModalProps } from "../interfaces/partialCancellation";
import { ProductSelectionStep } from "../../../../components/PartialCancellation/ProductSelectionStep";
import { PaymentDistributionStep } from "../../../../components/PartialCancellation/PaymentDistributionStep";
import { PhoneNumberStep } from "../../../../components/PartialCancellation/PhoneNumberStep";
import { usePartialCancellation } from "../hooks/usePartialCancellation";

const STEP_TITLES: Record<string, string> = {
  products: "Seleccionar productos",
  payments: "Distribución de reembolso",
  phone: "Número de WhatsApp",
  comment: "Motivo de cancelación",
  confirm: "Confirmar cancelación parcial",
};

export const PartialCancellationModal: React.FC<
  PartialCancellationModalProps
> = ({ open, closeModal, products, payments, saleUuid, saleCode, onSuccess }) => {
  const {
    step,
    selectedProducts,
    payments: refundablePayments,
    paymentRefunds,
    phoneNumber,
    refundTotal,
    isLoading,
    isAllSelected,
    hasSelectedProducts,
    isPaymentDistributionValid,
    isPhoneNumberValid,
    handleProductSelection,
    handlePaymentRefund,
    setPhoneNumber,
    setPhoneMatch,
    comment,
    setComment,
    goToNextStep,
    goToPreviousStep,
    submit,
    reset,
  } = usePartialCancellation({
    products,
    payments,
    saleUuid,
    saleCode,
    onSuccess,
    closeModal,
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  return (
    <Modal
      classNames={{
        modal: "bg-white w-auto sm:w-[550px] rounded-2xl shadow-modal !p-0",
        overlay: "bg-modal-overlay",
      }}
      showCloseIcon={false}
      open={open}
      onClose={closeModal}
      center
      focusTrapped={false}
    >
      <div className="p-8 sm:p-8 sm:pb-6">
        <h1 className="border-b border-gray-200 pb-4 text-xl font-bold text-gray-900">
          {STEP_TITLES[step]}
        </h1>

        <div className="mb-6 mt-6">
          {step === "products" && (
            <ProductSelectionStep
              products={products}
              selectedProducts={selectedProducts}
              onSelectionChange={handleProductSelection}
              refundTotal={refundTotal}
            />
          )}

          {step === "payments" && (
            <PaymentDistributionStep
              payments={refundablePayments}
              refundTotal={refundTotal}
              paymentRefunds={paymentRefunds}
              onRefundChange={handlePaymentRefund}
            />
          )}

          {step === "phone" && (
            <PhoneNumberStep
              onPhoneNumberChange={setPhoneNumber}
              onMatchChange={setPhoneMatch}
              refundTotal={refundTotal}
            />
          )}

          {step === "comment" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Escribe el motivo por el cual se realiza esta cancelación.{payments.length > 0 ? " Este campo es obligatorio." : " Este campo es opcional."}
              </p>
              <textarea
                className="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
                rows={3}
                maxLength={500}
                placeholder="Escribe el motivo de la cancelación..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-4">
              <p className="text-gray-600">
                {isAllSelected ? (
                  <>
                    Se cancelarán{" "}
                    <span className="font-bold text-black">
                      todos los productos
                    </span>{" "}
                    de la venta{" "}
                    <span className="font-bold text-black">{saleCode}</span>.
                    Esta acción es irreversible.
                  </>
                ) : (
                  <>
                    Se cancelarán los productos seleccionados de la venta{" "}
                    <span className="font-bold text-black">{saleCode}</span> por
                    un total de{" "}
                    <span className="font-bold text-red-600">
                      ${refundTotal.toFixed(2)}
                    </span>
                    . Esta acción es irreversible.
                  </>
                )}
              </p>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg bg-gray-50 p-3">
                {Object.entries(selectedProducts)
                  .filter(([, qty]) => qty > 0)
                  .map(([uuid, qty]) => {
                    const product = products.find((p) => p.uuid === uuid);
                    if (!product) return null;
                    return (
                      <div
                        key={uuid}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-gray-700">
                          {product.uuid_parent && "↳ "}
                          {product.product} × {qty}
                        </span>
                        <span className="font-medium text-red-600">
                          ${(product.price * qty).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
              </div>
              {phoneNumber && (
                <p className="text-sm text-gray-600">
                  Se enviará un mensaje de WhatsApp al{" "}
                  <span className="font-bold text-black">{phoneNumber}</span>{" "}
                  para solicitar los datos bancarios del cliente.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col justify-end gap-3 sm:flex-row">
          {step === "products" ? (
            <button
              className="h-12 rounded-lg border border-gray-200 bg-white px-6 py-2 text-sm text-gray-900 transition-colors duration-300 hover:bg-gray-100"
              onClick={closeModal}
            >
              Descartar
            </button>
          ) : (
            <button
              className="flex h-12 items-center gap-2 rounded-lg border border-gray-200 bg-white px-6 py-2 text-sm text-gray-900 transition-colors duration-300 hover:bg-gray-100"
              onClick={goToPreviousStep}
            >
              <ArrowLeft className="h-4 w-4" />
              Atrás
            </button>
          )}

          {step === "confirm" ? (
            <button
              className="flex h-12 items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-2 text-sm text-white transition-colors duration-300 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isLoading}
              onClick={submit}
            >
              {isLoading ? (
                <SpinnerCircularFixed
                  size={16}
                  color="#FFFFFF"
                  secondaryColor="#000000"
                />
              ) : (
                <Ban className="h-4 w-4 shrink-0" />
              )}
              Confirmar cancelación
            </button>
          ) : (
            <button
              className="flex h-12 items-center justify-center gap-2 rounded-lg bg-gray-900 px-6 py-2 text-sm text-white transition-colors duration-300 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={
                (step === "products" && !hasSelectedProducts) ||
                (step === "payments" && !isPaymentDistributionValid) ||
                (step === "phone" && !isPhoneNumberValid) ||
                (step === "comment" && payments.length > 0 && !comment.trim())
              }
              onClick={goToNextStep}
            >
              Siguiente
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
